import React, { useEffect, useState, useCallback } from 'react';
import { Search, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../ui/table';
import { Skeleton } from '../ui/skeleton';
import communityRegistryApi, {
  HouseholdListItem,
  HouseholdFilterParams,
} from '../../services/communityRegistryApi';
import HouseholdDetailModal from './HouseholdDetailModal';

interface Props {
  refreshKey?: number;
}

const HouseholdListTab: React.FC<Props> = ({ refreshKey = 0 }) => {
  const [items, setItems] = useState<HouseholdListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<HouseholdFilterParams>({});
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const fetchList = useCallback(() => {
    setLoading(true);
    communityRegistryApi
      .listHouseholds({ ...filters, q: search || undefined }, page, pageSize)
      .then((res) => {
        setItems(res.items);
        setTotal(res.total);
        setError(null);
      })
      .catch((e) => setError(e?.response?.data?.detail || 'Failed to load households'))
      .finally(() => setLoading(false));
  }, [filters, search, page, pageSize]);

  useEffect(() => {
    fetchList();
  }, [fetchList, refreshKey]);

  useEffect(() => {
    setPage(1);
  }, [search, filters]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="flex flex-col gap-3 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by household or head name…"
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <FilterToggle
              label="Under 5"
              active={!!filters.has_under_5}
              onClick={() =>
                setFilters((f) => ({ ...f, has_under_5: f.has_under_5 ? undefined : true }))
              }
            />
            <FilterToggle
              label="Pregnant"
              active={!!filters.has_pregnant}
              onClick={() =>
                setFilters((f) => ({ ...f, has_pregnant: f.has_pregnant ? undefined : true }))
              }
            />
            <a
              href={communityRegistryApi.exportHouseholdsUrl({ ...filters, q: search || undefined })}
              target="_blank"
              rel="noreferrer"
            >
              <Button variant="outline" size="sm">
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            </a>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <Card>
        <CardContent className="pt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Household</TableHead>
                <TableHead>House #</TableHead>
                <TableHead>Registry Code</TableHead>
                <TableHead>Head of household</TableHead>
                <TableHead>Locality</TableHead>
                <TableHead>CHW</TableHead>
                <TableHead className="text-right">Members</TableHead>
                <TableHead className="text-right">U5</TableHead>
                <TableHead className="text-right">Pregnant</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 10 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-sm text-muted-foreground">
                    No households match the current filters.
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                items.map((hh) => (
                  <TableRow
                    key={hh.id}
                    className="cursor-pointer"
                    onClick={() => setSelectedId(hh.id)}
                  >
                    <TableCell className="font-medium">{hh.external_code}</TableCell>
                    <TableCell>{hh.house_number || '—'}</TableCell>
                    <TableCell className="font-mono text-xs">{hh.registry_code || '—'}</TableCell>
                    <TableCell>{hh.head_name}</TableCell>
                    <TableCell>{hh.locality || '—'}</TableCell>
                    <TableCell>{hh.assigned_chw_name || '—'}</TableCell>
                    <TableCell className="text-right">{hh.member_count}</TableCell>
                    <TableCell className="text-right">{hh.under_5_count}</TableCell>
                    <TableCell className="text-right">{hh.pregnant_count}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>

          <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
            <div>
              {total > 0
                ? `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} of ${total}`
                : 'No results'}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span>
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {selectedId !== null && (
        <HouseholdDetailModal
          householdId={selectedId}
          onClose={() => setSelectedId(null)}
          onUpdated={fetchList}
        />
      )}
    </div>
  );
};

const FilterToggle: React.FC<{ label: string; active: boolean; onClick: () => void }> = ({
  label,
  active,
  onClick,
}) => (
  <Button variant={active ? 'default' : 'outline'} size="sm" onClick={onClick}>
    {label}
  </Button>
);

export default HouseholdListTab;
